<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { CloseIcon } from 'mdi-vue3'
import { computed, type HTMLAttributes } from 'vue'
import { useI18n } from 'vue-i18n'

import { cn } from '../../../lib/utils'
import type { Testable } from '../../../main'
import AIconButton from '../AButton/AIconButton.vue'

export interface RogueInputProperties extends Testable {
  defaultValue?: string | number
  modelValue?: string | number
  placeholder?: string | undefined
  class?: HTMLAttributes['class']
  disabled?: boolean
  type?: 'text' | 'password' | 'email' | 'number' | 'time' | 'date'
  readonly?: boolean
  limit?: number
  error?: string
  /** @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token_list_tokens */
  autocomplete?: boolean | string | undefined
  clearable?: boolean
  autofocus?: boolean
  clearLabel?: string
  preventDefaultEvents?: boolean
}

const properties = defineProps<RogueInputProperties>()

const emit = defineEmits<{
  (event: 'update:modelValue', payload: string | number): void
  (event: 'clear'): void
}>()

const modelValue = useVModel(properties, 'modelValue', emit, {
  passive: true,
  defaultValue: properties.defaultValue,
})

const { t } = useI18n({
  messages: {
    en: {
      clear: 'Clear field',
    },
    nl: {
      clear: 'Veld legen',
    },
  },
})

function onClear() {
  modelValue.value = ''
  emit('clear')
}

const computedAutocomplete = computed(() => {
  if (typeof properties.autocomplete === 'boolean') {
    return properties.autocomplete ? 'on' : 'off'
  }

  return properties.autocomplete
})
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/form-control-has-label -->
  <!-- eslint-disable vuejs-accessibility/no-autofocus -->
  <div class="atabix-input-wrap">
    <input
      v-model="modelValue"
      :autocomplete="computedAutocomplete"
      :autofocus="autofocus"
      v-bind="$attrs"
      :class="[cn('atabix-input', properties.class), { 'atabix-input--clearable': clearable, 'atabix-input--error': !!properties.error }]"
      :data-test
      :disabled="disabled"
      :limit="limit"
      :onKeyPress="(event) => (preventDefaultEvents ? event.stopimmediatepropagation() : undefined)"
      :placeholder="placeholder"
      :readonly="readonly"
      :type="properties.type"
    />
    <div v-if="clearable && modelValue" class="atabix-input-actions">
      <AIconButton class="atabix-input-actions__clear" :label="clearLabel || t('clear')" @click.stop="onClear">
        <CloseIcon />
      </AIconButton>
    </div>
  </div>
</template>

<style lang="scss">
.atabix-input-wrap {
  @apply relative;
}
.atabix-input {
  @apply flex;
  @apply h-10;
  @apply w-full;
  @apply rounded-md;
  @apply border;
  @apply border-border;
  @apply bg-background;
  @apply px-3;
  @apply py-2;
  @apply text-base;
  @apply ring-offset-background;
  @apply file:border-0;
  @apply file:bg-transparent;
  @apply file:text-base;
  @apply file:font-medium;
  @apply placeholder:text-foreground-placeholder;
  @apply focus-visible:outline-none;
  @apply focus-visible:border-form-border-selected;
  @apply disabled:cursor-not-allowed;
  @apply disabled:bg-background-disabled;
}
.atabix-input--clearable {
  @apply pr-20;
}
.atabix-input-actions {
  @apply absolute;
  @apply right-3;
  @apply top-0;
  @apply h-full;
  @apply flex;
  @apply items-center;
}
.atabix-input-actions__clear {
  @apply size-5;
  @apply cursor-pointer;

  &:hover {
    @apply text-foreground-subtle;
  }
}

.atabix-input--error {
  @apply border-form-border-error;
  @apply ring-form-border-error;
}
</style>
